import {type SubscribeToEvent} from "@next/eventservice/lib/esb"
import TrackGAEventWrapper from "../GA"
import PublishCountrySelectorOpen from "../../events/countrySelectorOpen"
import {SubscribeCountrySelectorClosed} from "../../events/countrySelectorClosed"
import {SubscribeCountrySelectorRedirect} from "../../events/countrySelectorRedirect"
import {fetchRetry} from "./utils"
import {SessionUpdate} from "./sessionUpdate"
import {GeolocationModel} from "../../models/geolocation"
import {getCountriesList} from "../../api/countries"
import {HTTPS_PROTOCOL} from "../../config/constants"
import {checkIfBot} from "../checkIfBot"

export const redirectToCorrectCountry = async (
    currentUrl: string,
    siteUrl: string,
    country: GeolocationModel,
    geolocationVersion: number,
    countrySelectorEndpoint: string,
) => {
    const countriesList = await getCountriesList(siteUrl, {}, countrySelectorEndpoint)
    let urlFromCountryList = countriesList.find(
        countryObj => countryObj.CountryCode?.toLowerCase() === country.ISOCountryCode?.toLowerCase(),
    )?.Languages[0].TargetUrl

    if (!urlFromCountryList) return

    urlFromCountryList = urlFromCountryList.startsWith("//")
        ? `${HTTPS_PROTOCOL}${urlFromCountryList}`
        : urlFromCountryList

    const constructRedirectUrl = () => {
        const currentPath = currentUrl.replace(`${siteUrl}`, "")
        return `${urlFromCountryList}/${currentPath.startsWith("/") ? currentPath.slice(1) : currentPath}`
    }

    const handleRedirect = async (url: string | undefined, page: string) => {
        console.log(url, page, country, geolocationVersion, siteUrl)
        await fetchRetry(async (attempt: number) => {
            await SessionUpdate(true, true, country, geolocationVersion, siteUrl, attempt)
        })
        TrackGAEventWrapper(
            "CountryRedirect",
            `Redirected to the Specific Country - ${page}`,
            country.ISOCountryName as string,
            0,
        )
        sessionStorage.setItem("geoLocationRedirect", JSON.stringify({isRedirected: true}))
        if (typeof window !== "undefined" && url) window.location.href = url
    }
    const url = constructRedirectUrl()
    await handleRedirect(url, "deep-linked page")
}

export const handleCountryChange = async (
    countryCode: string,
    country: GeolocationModel,
    GeolocationVersion: number,
    siteUrl: string,
    doCountrySelectorRedirect: boolean,
    countrySelectorEndpoint: string,
) => {
    // compare countryCode and country.ISOCountryCode
    /* 1. If in correct country, modal not required */
    /* 2. If the user is in the wrong country:
            2a. If they didn't come from the country selector page and redirection is enabled, redirect them to the correct country.
            2b. Show a modal with a specific message.
    */
    if (countryCode === "" || country.ISOCountryCode === "") return

    const geoLocationSSObj = (() => {
        const geoLocationRedirect = sessionStorage.getItem("geoLocationRedirect")
        return geoLocationRedirect ? JSON.parse(geoLocationRedirect) : null
    })()

    const isBot = checkIfBot()

    if (isBot) {
        TrackGAEventWrapper(
            "CountryRedirect",
            `Akamai - Identified as BOT (${window.navigator.userAgent})`,
            `BOT Origin - ${country.ISOCountryCode}`,
            0,
        )
        return
    }

    if (countryCode === country.ISOCountryCode) {
        await fetchRetry((attempt: number) =>
            SessionUpdate(false, false, country, GeolocationVersion, siteUrl, attempt),
        )
        return
    }

    if (doCountrySelectorRedirect && !geoLocationSSObj?.isRedirected) {
        await redirectToCorrectCountry(
            window.location.href,
            siteUrl,
            country,
            GeolocationVersion,
            countrySelectorEndpoint,
        )
    } else {
        PublishCountrySelectorOpen({isoCountryCode: country.ISOCountryCode as string})

        await fetchRetry(async (attempt: number) => {
            await SessionUpdate(true, true, country, GeolocationVersion, siteUrl, attempt)
            TrackGAEventWrapper("CountryRedirect", "Popup Displayed", country.ISOCountryName as string, 0)
        })

        let closedSubscription: SubscribeToEvent | null = null
        closedSubscription = SubscribeCountrySelectorClosed(() => {
            if (closedSubscription?.subscription) {
                // eslint-disable-next-line no-unused-expressions
                closedSubscription?.subscription?.unsubscribe?.()
            }

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            fetchRetry(async (attempt: number) => {
                await SessionUpdate(true, false, country, GeolocationVersion, siteUrl, attempt)
                TrackGAEventWrapper("CountryRedirect", "Closed Popup", country.ISOCountryName as string, 0)
            })
        })

        const redirectSubscription = SubscribeCountrySelectorRedirect(() => {
            if (redirectSubscription) redirectSubscription.subscription.unsubscribe()
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            fetchRetry(async (attempt: number) => {
                await SessionUpdate(true, true, country, GeolocationVersion, siteUrl, attempt)
                TrackGAEventWrapper(
                    "CountryRedirect",
                    "Redirected to Specific Country",
                    country.ISOCountryName as string,
                    0,
                )
            })
        })
    }
}
